/**
 * ============================================================================
 * Iron Meridian - Diplomacy Engine (Client-Side)
 * ============================================================================
 * 
 * PURPOSE:
 * Pure, deterministic diplomacy state management for all ISO-3 countries.
 * Handles trust generation, stance transitions, treaty management,
 * global reputation, and diplomatic actions.
 * 
 * DATA MODEL:
 * - Trust: -100 to +100 (integer, bilateral per country)
 * - Reputation: -100 to +100 (integer, global player standing)
 * - Stance: ALLY | FRIENDLY | NEUTRAL | WARY | HOSTILE
 * - Treaties: Array of active agreements
 * 
 * DESIGN PRINCIPLES:
 * - All functions are pure and deterministic
 * - Seeded RNG for reproducible game states
 * - No server dependencies (offline-first)
 * - Future-compatible with multiplayer
 * 
 * ============================================================================
 */

// ============================================================================
// CONSTANTS & TYPES
// ============================================================================

/**
 * @typedef {'ALLY' | 'FRIENDLY' | 'NEUTRAL' | 'WARY' | 'HOSTILE'} DiplomaticStance
 */

/**
 * @typedef {'PARIAH' | 'DISLIKED' | 'NEUTRAL' | 'RESPECTED' | 'REVERED'} ReputationTier
 */

/**
 * @typedef {'ALLIANCE' | 'NAP' | 'TRADE' | 'EMBARGO'} TreatyType
 */

/**
 * @typedef {'OFFER_ALLIANCE' | 'OFFER_NAP' | 'BREAK_TREATY' | 'ISSUE_THREAT' | 'IMPROVE_RELATIONS'} DiplomacyActionType
 */

/**
 * @typedef {Object} GlobalReputation
 * @property {number} score - Global reputation score (-100 to +100)
 * @property {ReputationTier} tier - Current reputation tier
 */

/**
 * @typedef {Object} Treaty
 * @property {string} id - Unique treaty identifier
 * @property {TreatyType} type - Type of treaty
 * @property {number} startedTurn - Turn when treaty was formed
 * @property {number} [expiresTurn] - Turn when treaty expires (undefined = permanent)
 */

/**
 * @typedef {Object} DiplomacyEntry
 * @property {string} iso3 - ISO-3 country code
 * @property {number} trust - Trust value (-100 to +100)
 * @property {DiplomaticStance} stance - Current diplomatic stance
 * @property {Treaty[]} treaties - Active treaties
 * @property {number} lastInteractionTurn - Last turn with diplomatic interaction
 * @property {boolean} [betrayalFlag] - Set if player broke treaty with this country
 */

export const DIPLOMATIC_STANCES = {
  ALLY: { id: 'ALLY', label: 'Allied', color: 'text-green-400', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/50', icon: '🛡️' },
  FRIENDLY: { id: 'FRIENDLY', label: 'Friendly', color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/50', icon: '😊' },
  NEUTRAL: { id: 'NEUTRAL', label: 'Neutral', color: 'text-gray-400', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/30', icon: '⚪' },
  WARY: { id: 'WARY', label: 'Wary', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500/50', icon: '⚠️' },
  HOSTILE: { id: 'HOSTILE', label: 'Hostile', color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50', icon: '⚔️' }
}

export const TREATY_TYPES = {
  ALLIANCE: { id: 'ALLIANCE', label: 'Alliance', icon: '🛡️', description: 'Mutual defense pact' },
  NAP: { id: 'NAP', label: 'Non-Aggression Pact', icon: '📜', description: 'Agreement not to attack' },
  TRADE: { id: 'TRADE', label: 'Trade Agreement', icon: '💰', description: 'Economic cooperation' },
  EMBARGO: { id: 'EMBARGO', label: 'Embargo', icon: '🚫', description: 'Economic sanctions' }
}

// Trust change reasons with their delta values
export const TRUST_MODIFIERS = {
  DECLARE_WAR: { delta: -40, reason: 'Declared war' },
  HONOR_TREATY: { delta: +15, reason: 'Honored treaty' },
  BREAK_PACT: { delta: -30, reason: 'Broke pact' },
  SIGN_TREATY: { delta: +20, reason: 'Signed treaty' },
  IGNORE_EVENT: { delta: -10, reason: 'Ignored diplomatic event' },
  PROVIDE_AID: { delta: +10, reason: 'Provided humanitarian aid' },
  TRADE_SUCCESS: { delta: +5, reason: 'Successful trade' },
  BORDER_INCIDENT: { delta: -15, reason: 'Border incident' },
  JOINT_EXERCISE: { delta: +8, reason: 'Joint military exercise' },
  ESPIONAGE_CAUGHT: { delta: -25, reason: 'Espionage discovered' }
}

// ============================================================================
// REPUTATION SYSTEM
// ============================================================================

export const REPUTATION_TIERS = {
  REVERED: { id: 'REVERED', label: 'Revered', minScore: 70, color: 'text-green-400', icon: '👑', trustModifier: 0.20 },
  RESPECTED: { id: 'RESPECTED', label: 'Respected', minScore: 30, color: 'text-blue-400', icon: '⭐', trustModifier: 0.10 },
  NEUTRAL: { id: 'NEUTRAL', label: 'Neutral', minScore: -29, color: 'text-gray-400', icon: '⚖️', trustModifier: 0 },
  DISLIKED: { id: 'DISLIKED', label: 'Disliked', minScore: -69, color: 'text-orange-400', icon: '👎', trustModifier: -0.15 },
  PARIAH: { id: 'PARIAH', label: 'Pariah', minScore: -100, color: 'text-red-400', icon: '💀', trustModifier: -0.30 }
}

// Reputation change reasons with their delta values
export const REPUTATION_MODIFIERS = {
  DECLARE_WAR: { delta: -10, reason: 'Declared war' },
  BREAK_ALLIANCE: { delta: -25, reason: 'Broke alliance' },
  BREAK_NAP: { delta: -15, reason: 'Broke non-aggression pact' },
  HONOR_TREATY: { delta: +8, reason: 'Honored treaty commitment' },
  AID_DISASTER: { delta: +12, reason: 'Provided disaster relief' },
  CAPTURE_CAPITAL: { delta: -15, reason: 'Captured enemy capital' },
  RAPID_EXPANSION: { delta: -8, reason: 'Rapid territorial expansion' },
  PEACE_BROKER: { delta: +10, reason: 'Brokered peace' },
  THREAT_ISSUED: { delta: -5, reason: 'Issued diplomatic threat' },
  IMPROVE_RELATIONS: { delta: +3, reason: 'Improved diplomatic relations' }
}

// ============================================================================
// DIPLOMACY ACTIONS
// ============================================================================

export const DIPLOMACY_ACTIONS = {
  OFFER_ALLIANCE: {
    id: 'OFFER_ALLIANCE',
    label: 'Offer Alliance',
    icon: '🛡️',
    description: 'Propose a mutual defense pact',
    requiresTrust: 40,
    onSuccess: { trustDelta: +15, reputationDelta: +5 },
    onReject: { trustDelta: -5, reputationDelta: 0 },
    cooldownTurns: 5
  },
  OFFER_NAP: {
    id: 'OFFER_NAP',
    label: 'Offer Non-Aggression Pact',
    icon: '📜',
    description: 'Propose an agreement not to attack',
    requiresTrust: 20,
    onSuccess: { trustDelta: +10, reputationDelta: +3 },
    onReject: { trustDelta: -5, reputationDelta: 0 },
    cooldownTurns: 3
  },
  BREAK_TREATY: {
    id: 'BREAK_TREATY',
    label: 'Break Treaty',
    icon: '💔',
    description: 'Renounce existing treaty (reputation penalty)',
    requiresTrust: -100, // Always available
    onSuccess: { trustDelta: -30, reputationDelta: -15 },
    onReject: null, // Cannot be rejected
    cooldownTurns: 0
  },
  ISSUE_THREAT: {
    id: 'ISSUE_THREAT',
    label: 'Issue Threat',
    icon: '⚠️',
    description: 'Threaten military action (may intimidate or anger)',
    requiresTrust: -100, // Always available
    onSuccess: { trustDelta: -10, reputationDelta: -5 },
    onReject: { trustDelta: -15, reputationDelta: -3 },
    cooldownTurns: 2
  },
  IMPROVE_RELATIONS: {
    id: 'IMPROVE_RELATIONS',
    label: 'Improve Relations',
    icon: '🤝',
    description: 'Send diplomatic envoys to improve trust',
    requiresTrust: -100, // Always available
    onSuccess: { trustDelta: +8, reputationDelta: +2 },
    onReject: { trustDelta: 0, reputationDelta: 0 },
    cooldownTurns: 1
  }
}

// Stance transition thresholds
const STANCE_THRESHOLDS = {
  ALLY: 60,
  FRIENDLY: 20,
  NEUTRAL_HIGH: 19,
  NEUTRAL_LOW: -19,
  WARY: -20,
  HOSTILE: -60
}

// ============================================================================
// SEEDED RANDOM NUMBER GENERATOR (Mulberry32)
// ============================================================================

/**
 * Mulberry32 seeded PRNG - deterministic random number generation
 * @param {number} seed - Integer seed value
 * @returns {function(): number} - Function that returns random numbers [0, 1)
 */
export function createSeededRNG(seed) {
  let state = seed >>> 0 // Ensure unsigned 32-bit integer
  
  return function() {
    state = (state + 0x6D2B79F5) | 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Generate a hash from a string for seeding RNG
 * @param {string} str - String to hash
 * @returns {number} - 32-bit hash value
 */
export function hashString(str) {
  let hash = 2166136261
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/**
 * Create a deterministic seed from game seed and country code
 * @param {number} gameSeed - Base game seed
 * @param {string} iso3 - Country ISO-3 code
 * @returns {number} - Combined seed for this country
 */
export function createCountrySeed(gameSeed, iso3) {
  return hashString(`${gameSeed}-diplomacy-${iso3}`)
}

// ============================================================================
// REPUTATION SYSTEM
// ============================================================================

/**
 * Derive reputation tier from global reputation score
 * @param {number} score - Reputation score (-100 to +100)
 * @returns {ReputationTier}
 */
export function deriveReputationTier(score) {
  if (score >= REPUTATION_TIERS.REVERED.minScore) return 'REVERED'
  if (score >= REPUTATION_TIERS.RESPECTED.minScore) return 'RESPECTED'
  if (score >= REPUTATION_TIERS.NEUTRAL.minScore) return 'NEUTRAL'
  if (score >= REPUTATION_TIERS.DISLIKED.minScore) return 'DISLIKED'
  return 'PARIAH'
}

/**
 * Get reputation tier configuration
 * @param {number} score - Reputation score
 * @returns {Object} - Tier configuration with label, color, icon, trustModifier
 */
export function getReputationTierConfig(score) {
  const tier = deriveReputationTier(score)
  return REPUTATION_TIERS[tier]
}

/**
 * Initialize global reputation state
 * @returns {GlobalReputation}
 */
export function createInitialReputation() {
  return {
    score: 0,
    tier: 'NEUTRAL',
    history: [] // Track reputation changes for UI
  }
}

/**
 * Modify global reputation with clamping and history tracking
 * @param {GlobalReputation} reputation - Current reputation state
 * @param {number} delta - Change amount
 * @param {string} reason - Reason for change
 * @param {number} turn - Current turn number
 * @returns {GlobalReputation} - New reputation state
 */
export function modifyReputation(reputation, delta, reason, turn = 0) {
  const newScore = Math.max(-100, Math.min(100, reputation.score + delta))
  const newTier = deriveReputationTier(newScore)
  
  const historyEntry = {
    delta,
    reason,
    turn,
    oldScore: reputation.score,
    newScore,
    oldTier: reputation.tier,
    newTier
  }
  
  return {
    score: newScore,
    tier: newTier,
    history: [...reputation.history.slice(-19), historyEntry] // Keep last 20 entries
  }
}

/**
 * Get trust modifier based on reputation tier
 * Higher reputation = more favorable trust gains
 * @param {ReputationTier} tier 
 * @returns {number} - Multiplier for trust changes
 */
export function getReputationTrustModifier(tier) {
  const config = REPUTATION_TIERS[tier]
  return config ? config.trustModifier : 0
}

// ============================================================================
// STANCE LOGIC
// ============================================================================

/**
 * Derive diplomatic stance from trust value
 * @param {number} trust - Trust value (-100 to +100)
 * @returns {DiplomaticStance}
 */
export function deriveStanceFromTrust(trust) {
  if (trust >= STANCE_THRESHOLDS.ALLY) return 'ALLY'
  if (trust >= STANCE_THRESHOLDS.FRIENDLY) return 'FRIENDLY'
  if (trust >= STANCE_THRESHOLDS.WARY) return 'NEUTRAL'
  if (trust >= STANCE_THRESHOLDS.HOSTILE) return 'WARY'
  return 'HOSTILE'
}

/**
 * Get the order/rank of a stance for transition validation
 * @param {DiplomaticStance} stance 
 * @returns {number} - Rank (0 = HOSTILE, 4 = ALLY)
 */
function getStanceRank(stance) {
  const ranks = { HOSTILE: 0, WARY: 1, NEUTRAL: 2, FRIENDLY: 3, ALLY: 4 }
  return ranks[stance] ?? 2
}

/**
 * Check if a stance transition is valid (max 1 step per turn)
 * @param {DiplomaticStance} from - Current stance
 * @param {DiplomaticStance} to - Target stance
 * @returns {boolean}
 */
export function isValidStanceTransition(from, to) {
  return Math.abs(getStanceRank(from) - getStanceRank(to)) <= 1
}

/**
 * Get the next stance towards target (respecting 1-step rule)
 * @param {DiplomaticStance} current - Current stance
 * @param {DiplomaticStance} target - Target stance from trust
 * @returns {DiplomaticStance} - Next valid stance
 */
export function getNextStance(current, target) {
  const currentRank = getStanceRank(current)
  const targetRank = getStanceRank(target)
  
  if (currentRank === targetRank) return current
  
  const stanceOrder = ['HOSTILE', 'WARY', 'NEUTRAL', 'FRIENDLY', 'ALLY']
  const direction = targetRank > currentRank ? 1 : -1
  
  return stanceOrder[currentRank + direction]
}

// ============================================================================
// TRUST MODIFICATION
// ============================================================================

/**
 * Clamp trust to valid range
 * @param {number} trust 
 * @returns {number} - Clamped trust (-100 to +100)
 */
export function clampTrust(trust) {
  return Math.max(-100, Math.min(100, Math.round(trust)))
}

/**
 * Modify trust between player and target country
 * @param {Object} params
 * @param {Record<string, DiplomacyEntry>} params.diplomacy - Current diplomacy state
 * @param {string} params.targetISO - Target country ISO-3
 * @param {number} params.delta - Trust change amount
 * @param {string} params.reason - Reason for change
 * @param {number} params.turn - Current turn number
 * @param {GlobalReputation} [params.reputation] - Player's global reputation (optional)
 * @returns {{diplomacy: Record<string, DiplomacyEntry>, log: Object}} - Updated state and log entry
 */
export function modifyTrust({ diplomacy, targetISO, delta, reason, turn, reputation }) {
  const entry = diplomacy[targetISO]
  if (!entry) {
    console.warn(`[Diplomacy] No entry for ${targetISO}`)
    return { diplomacy, log: null }
  }
  
  const oldTrust = entry.trust
  const oldStance = entry.stance
  
  // Apply reputation modifier to trust changes
  // Positive reputation boosts positive changes, reduces negative ones
  // Negative reputation does the opposite
  let adjustedDelta = delta
  if (reputation) {
    const modifier = getReputationTrustModifier(reputation.tier)
    if (delta > 0) {
      // Positive change: scale by (1 + modifier)
      adjustedDelta = Math.round(delta * (1 + modifier))
    } else if (delta < 0) {
      // Negative change: scale by (1 - modifier)
      // So high reputation reduces negative impact, low reputation increases it
      adjustedDelta = Math.round(delta * (1 - modifier))
    }
  }
  
  const newTrust = clampTrust(oldTrust + adjustedDelta)
  
  // Calculate new stance (respecting 1-step transition rule)
  const targetStance = deriveStanceFromTrust(newTrust)
  const newStance = getNextStance(oldStance, targetStance)
  
  // Create updated entry
  const updatedEntry = {
    ...entry,
    trust: newTrust,
    stance: newStance,
    lastInteractionTurn: turn
  }
  
  // Create log entry
  const log = {
    iso3: targetISO,
    countryName: entry.countryName,
    oldTrust,
    newTrust,
    delta: adjustedDelta,
    originalDelta: delta,
    reputationAdjusted: delta !== adjustedDelta,
    reason,
    oldStance,
    newStance,
    stanceChanged: oldStance !== newStance,
    turn
  }
  
  return {
    diplomacy: {
      ...diplomacy,
      [targetISO]: updatedEntry
    },
    log
  }
}

/**
 * Apply multiple trust modifications in sequence
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {Array<{targetISO: string, delta: number, reason: string}>} modifications 
 * @param {number} turn 
 * @param {GlobalReputation} [reputation] - Optional reputation for scaling
 * @returns {{diplomacy: Record<string, DiplomacyEntry>, logs: Object[]}}
 */
export function applyTrustModifications(diplomacy, modifications, turn, reputation) {
  let currentDiplomacy = { ...diplomacy }
  const logs = []
  
  for (const mod of modifications) {
    const result = modifyTrust({
      diplomacy: currentDiplomacy,
      targetISO: mod.targetISO,
      delta: mod.delta,
      reason: mod.reason,
      turn,
      reputation
    })
    currentDiplomacy = result.diplomacy
    if (result.log) {
      logs.push(result.log)
    }
  }
  
  return { diplomacy: currentDiplomacy, logs }
}

// ============================================================================
// DIPLOMACY ACTION PROCESSING
// ============================================================================

/**
 * Check if a diplomacy action is available for a target country
 * @param {Object} params
 * @param {DiplomacyActionType} params.actionType - The action to check
 * @param {DiplomacyEntry} params.targetEntry - Target country's diplomacy entry
 * @param {Object} params.cooldowns - Map of actionType -> { targetISO -> lastUsedTurn }
 * @param {number} params.currentTurn - Current game turn
 * @returns {{ available: boolean, reason?: string }}
 */
export function canPerformDiplomacyAction({ actionType, targetEntry, cooldowns, currentTurn }) {
  const action = DIPLOMACY_ACTIONS[actionType]
  if (!action) {
    return { available: false, reason: 'Invalid action type' }
  }
  
  // Check trust requirement
  if (targetEntry.trust < action.requiresTrust) {
    return { 
      available: false, 
      reason: `Requires trust of ${action.requiresTrust} (current: ${targetEntry.trust})` 
    }
  }
  
  // Check cooldown
  const actionCooldowns = cooldowns?.[actionType]
  if (actionCooldowns) {
    const lastUsed = actionCooldowns[targetEntry.iso3]
    if (lastUsed !== undefined) {
      const turnsRemaining = action.cooldownTurns - (currentTurn - lastUsed)
      if (turnsRemaining > 0) {
        return { 
          available: false, 
          reason: `On cooldown (${turnsRemaining} turns remaining)` 
        }
      }
    }
  }
  
  // Special case: BREAK_TREATY requires an active treaty
  if (actionType === 'BREAK_TREATY' && targetEntry.treaties.length === 0) {
    return { available: false, reason: 'No active treaty to break' }
  }
  
  // Special case: OFFER_ALLIANCE fails if already allied
  if (actionType === 'OFFER_ALLIANCE' && targetEntry.treaties.some(t => t.type === 'ALLIANCE')) {
    return { available: false, reason: 'Already in an alliance' }
  }
  
  // Special case: OFFER_NAP fails if already has NAP or alliance
  if (actionType === 'OFFER_NAP') {
    const hasNapOrAlliance = targetEntry.treaties.some(t => 
      t.type === 'NON_AGGRESSION' || t.type === 'ALLIANCE'
    )
    if (hasNapOrAlliance) {
      return { available: false, reason: 'Already has non-aggression pact or alliance' }
    }
  }
  
  return { available: true }
}

/**
 * Calculate AI acceptance probability for a diplomacy offer
 * Uses deterministic calculation based on game state
 * @param {Object} params
 * @param {DiplomacyActionType} params.actionType
 * @param {DiplomacyEntry} params.targetEntry
 * @param {GlobalReputation} params.playerReputation
 * @param {number} params.gameSeed
 * @param {number} params.turn
 * @returns {{ acceptChance: number, factors: Object }}
 */
export function calculateDiplomacyAcceptance({ actionType, targetEntry, playerReputation, gameSeed, turn }) {
  const action = DIPLOMACY_ACTIONS[actionType]
  if (!action || action.onReject === null) {
    // Actions like BREAK_TREATY can't be rejected
    return { acceptChance: 1, factors: { automatic: true } }
  }
  
  // Base acceptance starts at 50%
  let baseChance = 0.5
  const factors = {}
  
  // Trust factor: Higher trust = higher acceptance
  // Trust of 60+ gives +20%, trust of -60 gives -20%
  const trustFactor = (targetEntry.trust / 100) * 0.2
  factors.trust = trustFactor
  baseChance += trustFactor
  
  // Reputation factor: Higher reputation = higher acceptance
  const reputationModifier = getReputationTrustModifier(playerReputation?.tier || 'NEUTRAL')
  factors.reputation = reputationModifier
  baseChance += reputationModifier
  
  // Stance factor
  const stanceFactors = {
    ALLY: 0.15,
    FRIENDLY: 0.1,
    NEUTRAL: 0,
    WARY: -0.1,
    HOSTILE: -0.2
  }
  factors.stance = stanceFactors[targetEntry.stance] || 0
  baseChance += factors.stance
  
  // Action-specific modifiers
  if (actionType === 'OFFER_ALLIANCE') {
    // Alliances are harder to get
    factors.actionDifficulty = -0.15
    baseChance -= 0.15
  } else if (actionType === 'ISSUE_THREAT') {
    // Threats are more likely to backfire against strong nations
    // But work against weaker stances
    if (targetEntry.stance === 'HOSTILE' || targetEntry.stance === 'WARY') {
      factors.actionDifficulty = -0.1
      baseChance -= 0.1
    } else {
      factors.actionDifficulty = 0.05
      baseChance += 0.05
    }
  } else if (actionType === 'IMPROVE_RELATIONS') {
    // Improving relations is generally accepted unless hostile
    if (targetEntry.stance !== 'HOSTILE') {
      factors.actionDifficulty = 0.2
      baseChance += 0.2
    }
  }
  
  // Clamp to [0.05, 0.95] - always some chance either way
  const acceptChance = Math.max(0.05, Math.min(0.95, baseChance))
  
  return { acceptChance, factors }
}

/**
 * Resolve a diplomacy action with deterministic outcome
 * @param {Object} params
 * @param {DiplomacyActionType} params.actionType
 * @param {Record<string, DiplomacyEntry>} params.diplomacy
 * @param {string} params.targetISO
 * @param {GlobalReputation} params.reputation
 * @param {number} params.gameSeed
 * @param {number} params.turn
 * @returns {{ 
 *   diplomacy: Record<string, DiplomacyEntry>,
 *   reputation: GlobalReputation,
 *   outcome: Object 
 * }}
 */
export function resolveDiplomacyAction({ actionType, diplomacy, targetISO, reputation, gameSeed, turn }) {
  const action = DIPLOMACY_ACTIONS[actionType]
  const targetEntry = diplomacy[targetISO]
  
  if (!action || !targetEntry) {
    return { 
      diplomacy, 
      reputation, 
      outcome: { success: false, error: 'Invalid action or target' } 
    }
  }
  
  // Calculate acceptance (deterministic)
  const { acceptChance, factors } = calculateDiplomacyAcceptance({
    actionType,
    targetEntry,
    playerReputation: reputation,
    gameSeed,
    turn
  })
  
  // Deterministic roll based on seed
  const actionSeed = hashString(`${gameSeed}-action-${actionType}-${targetISO}-${turn}`)
  const rng = createSeededRNG(actionSeed)
  const roll = rng()
  const accepted = roll < acceptChance
  
  // Get the appropriate outcome
  const outcomeConfig = accepted ? action.onSuccess : action.onReject
  
  // If action cannot be rejected (like BREAK_TREATY), it always succeeds
  const finalAccepted = action.onReject === null ? true : accepted
  const finalOutcome = finalAccepted ? action.onSuccess : action.onReject
  
  // Apply trust change
  let newDiplomacy = diplomacy
  let trustLog = null
  if (finalOutcome?.trustDelta) {
    const result = modifyTrust({
      diplomacy,
      targetISO,
      delta: finalOutcome.trustDelta,
      reason: `${action.label} ${finalAccepted ? 'accepted' : 'rejected'}`,
      turn,
      reputation
    })
    newDiplomacy = result.diplomacy
    trustLog = result.log
  }
  
  // Apply reputation change
  let newReputation = reputation
  if (finalOutcome?.reputationDelta) {
    newReputation = modifyReputation(
      reputation,
      finalOutcome.reputationDelta,
      `${action.label} with ${targetEntry.countryName}`,
      turn
    )
  }
  
  // Handle treaty creation for successful alliance/NAP offers
  if (finalAccepted) {
    if (actionType === 'OFFER_ALLIANCE') {
      newDiplomacy = addTreaty(newDiplomacy, targetISO, {
        type: 'ALLIANCE',
        startTurn: turn,
        name: 'Mutual Defense Alliance'
      })
    } else if (actionType === 'OFFER_NAP') {
      newDiplomacy = addTreaty(newDiplomacy, targetISO, {
        type: 'NON_AGGRESSION',
        startTurn: turn,
        name: 'Non-Aggression Pact',
        duration: 10
      })
    } else if (actionType === 'BREAK_TREATY') {
      // Remove the first treaty (or could be smarter about which one)
      const treaties = newDiplomacy[targetISO]?.treaties || []
      if (treaties.length > 0) {
        newDiplomacy = removeTreaty(newDiplomacy, targetISO, treaties[0].id)
        // Mark betrayal flag
        newDiplomacy = {
          ...newDiplomacy,
          [targetISO]: {
            ...newDiplomacy[targetISO],
            betrayalFlag: true
          }
        }
      }
    }
  }
  
  return {
    diplomacy: newDiplomacy,
    reputation: newReputation,
    outcome: {
      actionType,
      targetISO,
      targetName: targetEntry.countryName,
      accepted: finalAccepted,
      acceptChance,
      roll,
      factors,
      trustLog,
      reputationDelta: finalOutcome?.reputationDelta || 0,
      icon: action.icon,
      label: action.label
    }
  }
}

/**
 * Get available diplomacy actions for a target country
 * @param {DiplomacyEntry} targetEntry
 * @param {Object} cooldowns
 * @param {number} currentTurn
 * @returns {Array<{ action: Object, available: boolean, reason?: string }>}
 */
export function getAvailableDiplomacyActions(targetEntry, cooldowns, currentTurn) {
  return Object.entries(DIPLOMACY_ACTIONS).map(([actionType, action]) => {
    const check = canPerformDiplomacyAction({
      actionType,
      targetEntry,
      cooldowns,
      currentTurn
    })
    return {
      ...action,
      actionType,
      ...check
    }
  })
}

// ============================================================================
// INITIAL STATE GENERATION
// ============================================================================

/**
 * Generate initial diplomacy state for all countries
 * @param {Object} params
 * @param {Object} params.countries - Countries map (id -> country data)
 * @param {number} params.seed - Game seed for deterministic generation
 * @param {string} params.playerCountry - Player's country ISO-3
 * @returns {Record<string, DiplomacyEntry>}
 */
export function generateInitialDiplomacyState({ countries, seed, playerCountry }) {
  const diplomacy = {}
  
  for (const [iso3, country] of Object.entries(countries)) {
    // Skip player's own country
    if (iso3 === playerCountry) continue
    
    // Create deterministic seed for this country
    const countrySeed = createCountrySeed(seed, iso3)
    const rng = createSeededRNG(countrySeed)
    
    // Generate initial trust: -30 to +30 range
    // Using rng() which returns [0, 1), scale to [-30, +30]
    const initialTrust = Math.floor(rng() * 61) - 30
    
    // Derive initial stance (no ALLY or HOSTILE at game start)
    let stance
    if (initialTrust >= 15) {
      stance = 'FRIENDLY'
    } else if (initialTrust <= -15) {
      stance = 'WARY'
    } else {
      stance = 'NEUTRAL'
    }
    
    diplomacy[iso3] = {
      iso3,
      countryName: country.name || iso3,
      trust: initialTrust,
      stance,
      treaties: [],
      lastInteractionTurn: 0,
      // Extra metadata for UI
      controller: country.controller || 'neutral',
      personality: country.personality || null,
      military: country.military || 0,
      economy: country.economy || 0
    }
  }
  
  return diplomacy
}

// ============================================================================
// TURN PROCESSING
// ============================================================================

/**
 * Process diplomacy at end of turn
 * - Update stances to match trust (1-step max)
 * - Check treaty expirations
 * - Natural trust drift (optional)
 * 
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {number} turn 
 * @returns {{diplomacy: Record<string, DiplomacyEntry>, events: Object[]}}
 */
export function processDiplomacyTurn(diplomacy, turn) {
  const events = []
  const updatedDiplomacy = {}
  
  for (const [iso3, entry] of Object.entries(diplomacy)) {
    let updatedEntry = { ...entry }
    
    // Check if stance should transition towards trust-derived stance
    const targetStance = deriveStanceFromTrust(entry.trust)
    if (targetStance !== entry.stance) {
      const newStance = getNextStance(entry.stance, targetStance)
      if (newStance !== entry.stance) {
        events.push({
          type: 'STANCE_CHANGE',
          iso3,
          countryName: entry.countryName,
          from: entry.stance,
          to: newStance,
          trust: entry.trust,
          turn
        })
        updatedEntry.stance = newStance
      }
    }
    
    // Check treaty expirations
    const activeTreaties = []
    const treaties = entry.treaties || []
    for (const treaty of treaties) {
      if (treaty.expiresTurn && treaty.expiresTurn <= turn) {
        events.push({
          type: 'TREATY_EXPIRED',
          iso3,
          countryName: entry.countryName,
          treaty: treaty.type,
          turn
        })
      } else {
        activeTreaties.push(treaty)
      }
    }
    updatedEntry.treaties = activeTreaties
    
    updatedDiplomacy[iso3] = updatedEntry
  }
  
  return { diplomacy: updatedDiplomacy, events }
}

// ============================================================================
// TREATY MANAGEMENT
// ============================================================================

/**
 * Add a treaty with a country
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {string} targetISO 
 * @param {TreatyType} treatyType 
 * @param {number} turn 
 * @param {number} [duration] - Turns until expiration (undefined = permanent)
 * @returns {Record<string, DiplomacyEntry>}
 */
export function addTreaty(diplomacy, targetISO, treatyType, turn, duration) {
  const entry = diplomacy[targetISO]
  if (!entry) return diplomacy
  
  const treaty = {
    id: `${treatyType}-${targetISO}-${turn}`,
    type: treatyType,
    startedTurn: turn,
    expiresTurn: duration ? turn + duration : undefined
  }
  
  return {
    ...diplomacy,
    [targetISO]: {
      ...entry,
      treaties: [...entry.treaties, treaty],
      lastInteractionTurn: turn
    }
  }
}

/**
 * Remove a treaty with a country
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {string} targetISO 
 * @param {string} treatyId 
 * @returns {Record<string, DiplomacyEntry>}
 */
export function removeTreaty(diplomacy, targetISO, treatyId) {
  const entry = diplomacy[targetISO]
  if (!entry) return diplomacy
  
  return {
    ...diplomacy,
    [targetISO]: {
      ...entry,
      treaties: entry.treaties.filter(t => t.id !== treatyId)
    }
  }
}

// ============================================================================
// VALIDATION (Dev-only)
// ============================================================================

/**
 * Validate diplomacy state integrity
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {Object} countries - Expected countries
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateDiplomacyState(diplomacy, countries, playerCountry) {
  const errors = []
  
  // Check all countries have entries
  for (const iso3 of Object.keys(countries)) {
    if (iso3 === playerCountry) continue
    if (!diplomacy[iso3]) {
      errors.push(`Missing diplomacy entry for ${iso3}`)
    }
  }
  
  // Check all entries are valid
  for (const [iso3, entry] of Object.entries(diplomacy)) {
    // Trust bounds
    if (entry.trust < -100 || entry.trust > 100) {
      errors.push(`${iso3}: Trust out of bounds (${entry.trust})`)
    }
    
    // Stance validity
    if (!DIPLOMATIC_STANCES[entry.stance]) {
      errors.push(`${iso3}: Invalid stance (${entry.stance})`)
    }
    
    // Stance-trust mismatch warning (not error, due to 1-step rule)
    const expectedStance = deriveStanceFromTrust(entry.trust)
    const stanceDistance = Math.abs(getStanceRank(entry.stance) - getStanceRank(expectedStance))
    if (stanceDistance > 1) {
      errors.push(`${iso3}: Stance ${entry.stance} too far from trust-derived ${expectedStance}`)
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get all countries with a specific stance
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {DiplomaticStance} stance 
 * @returns {DiplomacyEntry[]}
 */
export function getCountriesByStance(diplomacy, stance) {
  return Object.values(diplomacy).filter(e => e.stance === stance)
}

/**
 * Get countries with active treaties
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @returns {DiplomacyEntry[]}
 */
export function getCountriesWithTreaties(diplomacy) {
  return Object.values(diplomacy).filter(e => e.treaties.length > 0)
}

/**
 * Get trust summary statistics
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @returns {{avg: number, min: number, max: number, byStance: Record<DiplomaticStance, number>}}
 */
export function getTrustStats(diplomacy) {
  const entries = Object.values(diplomacy)
  if (entries.length === 0) {
    return { avg: 0, min: 0, max: 0, byStance: {} }
  }
  
  const trusts = entries.map(e => e.trust)
  const byStance = {}
  
  for (const entry of entries) {
    byStance[entry.stance] = (byStance[entry.stance] || 0) + 1
  }
  
  return {
    avg: Math.round(trusts.reduce((a, b) => a + b, 0) / trusts.length),
    min: Math.min(...trusts),
    max: Math.max(...trusts),
    byStance
  }
}

/**
 * Filter and sort diplomacy entries for UI
 * @param {Record<string, DiplomacyEntry>} diplomacy 
 * @param {Object} options
 * @param {string} [options.search] - Search query
 * @param {DiplomaticStance[]} [options.stances] - Filter by stances
 * @param {string} [options.controllerType] - 'ai' | 'neutral' | 'all'
 * @param {[number, number]} [options.trustRange] - [min, max] trust filter
 * @param {string} [options.sortBy] - 'name' | 'trust' | 'military' | 'economy' | 'lastInteraction'
 * @param {boolean} [options.sortDesc] - Sort descending
 * @returns {DiplomacyEntry[]}
 */
export function filterAndSortDiplomacy(diplomacy, options = {}) {
  let entries = Object.values(diplomacy)
  
  // Search filter
  if (options.search) {
    const query = options.search.toLowerCase()
    entries = entries.filter(e => 
      e.countryName?.toLowerCase().includes(query) ||
      e.iso3?.toLowerCase().includes(query)
    )
  }
  
  // Stance filter
  if (options.stances && options.stances.length > 0) {
    entries = entries.filter(e => options.stances.includes(e.stance))
  }
  
  // Controller filter
  if (options.controllerType && options.controllerType !== 'all') {
    entries = entries.filter(e => e.controller === options.controllerType)
  }
  
  // Trust range filter
  if (options.trustRange) {
    const [min, max] = options.trustRange
    entries = entries.filter(e => e.trust >= min && e.trust <= max)
  }
  
  // Sorting
  const sortBy = options.sortBy || 'name'
  const sortDesc = options.sortDesc ?? false
  
  entries.sort((a, b) => {
    let cmp = 0
    switch (sortBy) {
      case 'trust':
        cmp = a.trust - b.trust
        break
      case 'military':
        cmp = (a.military || 0) - (b.military || 0)
        break
      case 'economy':
        cmp = (a.economy || 0) - (b.economy || 0)
        break
      case 'lastInteraction':
        cmp = (a.lastInteractionTurn || 0) - (b.lastInteractionTurn || 0)
        break
      case 'name':
      default:
        cmp = (a.countryName || '').localeCompare(b.countryName || '')
    }
    return sortDesc ? -cmp : cmp
  })
  
  return entries
}
